// Iter 82.8 Sprint 2 / Phase E3.0 — Payroll Compliance Settings.
// Surfaces the active compliance document with all sections rendered:
// PF, ESI, PT slabs by state, LWF, Gratuity, TDS slabs (Old + New
// regime), Bonus. Read-mostly UI — operators "supersede" with a new
// effective-dated document rather than editing the active one (per
// the historical-reproducibility invariant).
import { useCallback, useEffect, useState } from "react";
import { Calendar, Sparkle, ChartBar } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const pct = (n) => `${(Number(n || 0) * 100).toFixed(2)}%`;

export default function PayrollCompliance() {
    const [doc, setDoc] = useState(null);
    const [allDocs, setAllDocs] = useState([]);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [cur, list] = await Promise.all([
                api.get("/payroll/compliance/current"),
                api.get("/payroll/compliance/list"),
            ]);
            setDoc(cur.data);
            setAllDocs(list.data.items);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load compliance settings"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    if (loading || !doc) {
        return (
            <div className="px-6 sm:px-8 py-6">
                <PageHeader eyebrow="Payroll Configuration" title="Compliance Settings" />
                <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>
            </div>
        );
    }

    const states = Object.keys(doc.pt_slabs_by_state || {});

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="payroll-compl-page">
            <PageHeader
                eyebrow="Payroll Configuration"
                title="Compliance Settings"
                description={`Effective from ${formatDate(doc.effective_from)}${doc.effective_to ? ` to ${formatDate(doc.effective_to)}` : " (active)"} · ${allDocs.length} historical version${allDocs.length === 1 ? "" : "s"}`}
            />

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card title="Provident Fund (PF)" testid="compl-pf">
                    <Row k="Employee Rate" v={pct(doc.pf.ee_rate)} />
                    <Row k="Employer Rate" v={pct(doc.pf.er_rate)} />
                    <Row k="Wage Ceiling" v={inr(doc.pf.wage_ceiling)} />
                    <Row k="Monthly Cap (EE)" v={inr(doc.pf.ee_monthly_cap)} />
                    <Row k="Monthly Cap (ER)" v={inr(doc.pf.er_monthly_cap)} />
                    <Row k="Admin Charges" v={pct(doc.pf.admin_charges_rate)} />
                    <Row k="Status" v={<Badge className={doc.pf.is_enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100"}>{doc.pf.is_enabled ? "Enabled" : "Disabled"}</Badge>} />
                </Card>

                <Card title="Employees' State Insurance (ESI)" testid="compl-esi">
                    <Row k="Employee Rate" v={pct(doc.esi.ee_rate)} />
                    <Row k="Employer Rate" v={pct(doc.esi.er_rate)} />
                    <Row k="Gross Ceiling" v={inr(doc.esi.gross_ceiling)} />
                    <Row k="Status" v={<Badge className={doc.esi.is_enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100"}>{doc.esi.is_enabled ? "Enabled" : "Disabled"}</Badge>} />
                </Card>

                <Card title="Gratuity" testid="compl-gratuity">
                    <Row k="CTC Accrual Rate" v={pct(doc.gratuity.ctc_accrual_rate)} />
                    <Row k="Minimum Service" v={`${doc.gratuity.minimum_service_years} year${doc.gratuity.minimum_service_years !== 1 ? "s" : ""}`} />
                </Card>

                <Card title="Statutory Bonus" testid="compl-bonus">
                    <Row k="Wage Ceiling" v={inr(doc.bonus.wage_ceiling)} />
                    <Row k="Min Rate" v={pct(doc.bonus.min_rate)} />
                    <Row k="Max Rate" v={pct(doc.bonus.max_rate)} />
                </Card>
            </div>

            <Card title="Professional Tax (PT) — Slabs by State" testid="compl-pt" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="compl-pt-states">
                    {states.map((st) => (
                        <div key={st} className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid={`pt-state-${st}`}>
                            <div className="text-xs font-semibold text-slate-800 mb-2">{st}</div>
                            <table className="w-full text-[10px]">
                                <thead className="text-slate-500">
                                    <tr>
                                        <th className="text-left py-0.5">Gross Range</th>
                                        <th className="text-right py-0.5">Tax</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(doc.pt_slabs_by_state[st] || []).map((slab, i) => (
                                        <tr key={i}>
                                            <td className="py-0.5 font-mono">{inr(slab.min_gross)} – {slab.max_gross ? inr(slab.max_gross) : "∞"}</td>
                                            <td className="py-0.5 text-right font-mono">{inr(slab.monthly_tax)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </Card>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card title="TDS Slabs — Old Regime" testid="compl-tds-old">
                    <SlabTable slabs={doc.tds.old_regime_slabs} />
                    <div className="mt-2 text-xs text-slate-600">
                        Section 87A: Rebate up to <strong>{inr(doc.tds.section_87a_old_rebate)}</strong> if taxable income ≤ <strong>{inr(doc.tds.section_87a_old_max_income)}</strong>
                    </div>
                </Card>
                <Card title="TDS Slabs — New Regime" testid="compl-tds-new">
                    <SlabTable slabs={doc.tds.new_regime_slabs} />
                    <div className="mt-2 text-xs text-slate-600">
                        Section 87A: Rebate up to <strong>{inr(doc.tds.section_87a_new_rebate)}</strong> if taxable income ≤ <strong>{inr(doc.tds.section_87a_new_max_income)}</strong>
                    </div>
                </Card>
            </div>

            <Card title="Common TDS Settings" testid="compl-tds-common" className="mt-4">
                <Row k="Standard Deduction" v={inr(doc.tds.standard_deduction)} />
                <Row k="Cess Rate" v={pct(doc.tds.cess_rate)} />
            </Card>

            {allDocs.length > 1 && (
                <Card title={`History · ${allDocs.length} versions`} testid="compl-history" className="mt-4">
                    <table className="w-full text-xs">
                        <thead className="text-slate-500">
                            <tr>
                                <th className="text-left py-1.5">ID</th>
                                <th className="text-left py-1.5">Effective From → To</th>
                                <th className="text-left py-1.5">Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allDocs.map((d) => (
                                <tr key={d.id} className="border-t border-slate-100" data-testid={`compl-hist-${d.id}`}>
                                    <td className="py-1.5 font-mono">{d.id}</td>
                                    <td className="py-1.5">{formatDate(d.effective_from)} → {d.effective_to ? formatDate(d.effective_to) : "—"}</td>
                                    <td className="py-1.5">{d.description}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}
        </div>
    );
}

function Card({ title, children, testid, className = "" }) {
    return (
        <section className={`bg-white rounded-xl border border-slate-200 p-4 ${className}`} data-testid={testid}>
            <h3 className="text-sm font-semibold text-slate-800 mb-2">{title}</h3>
            {children}
        </section>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between text-xs py-1 border-t border-slate-100 first:border-t-0">
            <span className="text-slate-600">{k}</span>
            <span className="font-mono font-semibold">{v}</span>
        </div>
    );
}

function SlabTable({ slabs }) {
    return (
        <table className="w-full text-xs">
            <thead className="text-slate-500">
                <tr>
                    <th className="text-left py-1">Income Range</th>
                    <th className="text-right py-1">Rate</th>
                </tr>
            </thead>
            <tbody>
                {(slabs || []).map((s, i) => (
                    <tr key={i} className="border-t border-slate-100">
                        <td className="py-1 font-mono">{`₹${(s.min_income / 100000).toFixed(1)}L`} – {s.max_income ? `₹${(s.max_income / 100000).toFixed(1)}L` : "∞"}</td>
                        <td className="py-1 text-right font-mono">{pct(s.rate)}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
